import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { SolarProduct } from '@solar/contracts';

import { Button } from '../../../components/Button';
import { catalogueApi } from '../../../features/catalogue/catalogue.api';
import { formatNgn } from '../../../lib/format-currency';
import { routeTo } from '../../../routes/paths';

import styles from './ProductsPage.module.css';

const PAGE_SIZE = 9;
const SKELETON_COUNT = 6;

function customerLabel(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

function ProductCard({ product }: { readonly product: SolarProduct }): React.JSX.Element {
  return (
    <article className={styles.productCard}>
      <div className={styles.productVisual}>
        <span className={styles.category}>{customerLabel(product.category)}</span>
        <div className={styles.capacityBlock}>
          <strong className={styles.capacity}>{product.systemCapacityKw} kW</strong>
          <span className={styles.capacityLabel}>Solar system</span>
        </div>
      </div>

      <div className={styles.productBody}>
        <div className={styles.productCopy}>
          <h2 className={styles.productName}>{product.name}</h2>
          <p className={styles.description}>{product.description}</p>
        </div>

        <dl className={styles.commercialDetails}>
          <div>
            <dt>Base price</dt>
            <dd>{formatNgn(product.basePrice)}</dd>
          </div>
          {product.warrantySummary !== null && product.warrantySummary.trim() !== '' && (
            <div>
              <dt>Warranty</dt>
              <dd>{product.warrantySummary}</dd>
            </div>
          )}
        </dl>

        <Link className={styles.detailsLink} to={routeTo.customerProduct(product.id)}>
          View details <span aria-hidden="true">→</span>
        </Link>
      </div>
    </article>
  );
}

function ProductSkeletons(): React.JSX.Element {
  return (
    <section className={styles.productGrid} aria-label="Loading solar products" aria-busy="true">
      {Array.from({ length: SKELETON_COUNT }, (_, index) => (
        <div className={styles.skeletonCard} key={index} aria-hidden="true">
          <div className={styles.skeletonVisual} />
          <div className={styles.skeletonBody}>
            <span className={styles.skeletonLineShort} />
            <span className={styles.skeletonLine} />
            <span className={styles.skeletonLine} />
            <span className={styles.skeletonPrice} />
          </div>
        </div>
      ))}
    </section>
  );
}

export function ProductsPage(): React.JSX.Element {
  const [page, setPage] = useState(1);
  const products = useQuery({
    queryKey: ['catalogue', 'products', 'customer', page],
    queryFn: () => catalogueApi.listCustomerProducts({ page, pageSize: PAGE_SIZE }),
  });

  const pagination = products.data?.pagination;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <p className={styles.eyebrow}>Our catalogue</p>
        <h1 className={styles.pageTitle}>Solar solutions</h1>
        <p className={styles.pageSubtitle}>
          Find the right solar system for your home or business.
        </p>
      </header>

      {products.isPending && <ProductSkeletons />}

      {products.isError && (
        <section className={styles.stateCard} aria-labelledby="products-error-title">
          <div className={styles.stateMark} aria-hidden="true">
            !
          </div>
          <h2 className={styles.stateTitle} id="products-error-title">
            We couldn&apos;t load the catalogue
          </h2>
          <p className={styles.stateBody}>Please try again to see the available solar products.</p>
          <Button
            busy={products.isFetching}
            onClick={() => {
              void products.refetch();
            }}
          >
            Retry
          </Button>
        </section>
      )}

      {products.isSuccess && products.data.items.length === 0 && (
        <section className={styles.stateCard} aria-labelledby="products-empty-title">
          <div className={styles.emptyMark} aria-hidden="true" />
          <h2 className={styles.stateTitle} id="products-empty-title">
            No solar products are available right now.
          </h2>
        </section>
      )}

      {products.isSuccess && products.data.items.length > 0 && (
        <section className={styles.catalogue} aria-labelledby="available-products-title">
          <div className={styles.catalogueHeader}>
            <div>
              <h2 className={styles.catalogueTitle} id="available-products-title">
                Available systems
              </h2>
              <p className={styles.resultCount}>
                {products.data.pagination.totalItems}{' '}
                {products.data.pagination.totalItems === 1 ? 'solution' : 'solutions'} available
              </p>
            </div>
          </div>

          <div className={styles.productGrid}>
            {products.data.items.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {pagination !== undefined && pagination.totalPages > 1 && (
            <nav className={styles.pagination} aria-label="Product catalogue pages">
              <Button
                variant="secondary"
                disabled={!pagination.hasPreviousPage || products.isFetching}
                onClick={() => {
                  setPage((current) => Math.max(1, current - 1));
                }}
              >
                Previous
              </Button>
              <p className={styles.pageCount} aria-live="polite">
                Page {pagination.page} of {pagination.totalPages}
              </p>
              <Button
                variant="secondary"
                disabled={!pagination.hasNextPage || products.isFetching}
                onClick={() => {
                  setPage((current) => current + 1);
                }}
              >
                Next
              </Button>
            </nav>
          )}
        </section>
      )}
    </div>
  );
}
